import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTradingQuery } from '../hooks/useTradingQuery';
import { Plus, RefreshCw, AlertTriangle, ChevronRight, ShieldAlert } from 'lucide-react';
import { tradingService, type WebhookStock, type TradeAggregate } from '../services/trading';
import { accountsService } from '../services/accounts';
import { SellPositionDialog } from '../components/webhooks/SellPositionDialog';
import { BottomSheet } from '../components/ui/BottomSheet';

type FilterType = 'all' | 'active' | 'blocked';

interface ColumnVisibility {
  ticker: boolean;
  account: boolean;
  algorithm: boolean;
  units: boolean;
  position: boolean;
  winRate: boolean;
  totalPnl: boolean;
  lastTrade: boolean;
  trades: boolean;
  actions: boolean;
}

export default function WebhookStocksPage() {
  const queryClient = useQueryClient();
  const { setIsTradeDialogOpen } = useOutletContext<{ setIsTradeDialogOpen: (open: boolean) => void }>();
  const [filter, setFilter] = useState<FilterType>('all');
  const [confirmBlock, setConfirmBlock] = useState<{ stock: WebhookStock; blocked: boolean } | null>(null);
  const [sellStock, setSellStock] = useState<WebhookStock | null>(null);
  const [sellError, setSellError] = useState<string | null>(null);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>({
    ticker: true,
    account: true,
    algorithm: true,
    units: true,
    position: false,
    winRate: false,
    totalPnl: true,
    lastTrade: false,
    trades: true,
    actions: true,
  });
  
  // Track which stock's units is being edited
  const [editingUnitsKey, setEditingUnitsKey] = useState<string | null>(null);
  const [editUnitsValue, setEditUnitsValue] = useState<string>('');
  
  // Mobile position detail sheet
  const [selectedPosition, setSelectedPosition] = useState<{
    agg: TradeAggregate;
    accountName: string;
    brokerageName: string;
    quantity: number;
    avgPrice: number;
  } | null>(null);
  
  // Mobile webhook stock detail sheet
  const [selectedStock, setSelectedStock] = useState<WebhookStock | null>(null);

  // Success message after block/unblock
  const [blockSuccessMessage, setBlockSuccessMessage] = useState<string | null>(null);

  // Fetch webhook stocks (for All Webhook Stocks grid)
  const { data: stocks = [], isLoading, error } = useTradingQuery({
    queryKey: ['webhookStocks'],
    queryFn: tradingService.getWebhookStocks,
  });

  // Fetch open positions from trade_aggregates (single source of truth)
  const { data: openAggregates = [], isLoading: isLoadingAggregates, error: aggregatesError } = useTradingQuery({
    queryKey: ['tradeAggregates', 'open'],
    queryFn: async () => {
      console.log('[OpenPositions] Fetching from trade_aggregates...');
      try {
        const result = await tradingService.getOpenAggregates();
        console.log('[OpenPositions] Success! Received aggregates:', result.length);
        return result;
      } catch (error) {
        console.error('[OpenPositions] Error fetching aggregates:', error);
        throw error;
      }
    },
  });

  // Fetch broker positions for reconciliation check
  const { data: brokerPositions = [] } = useTradingQuery({
    queryKey: ['brokerPositions'],
    queryFn: async () => {
      try {
        return await accountsService.getOpenPositions();
      } catch (error) {
        console.error('[Reconciliation] Error fetching broker positions:', error);
        return [];
      }
    },
  });

  // Get account info for display (must be before any conditional returns!)
  const { data: accounts = [] } = useTradingQuery({
    queryKey: ['accounts'],
    queryFn: () => accountsService.getAccounts(),
  });

  // Regime-guard override state (+ current market regime for context)
  const { data: regimeBypass } = useTradingQuery({
    queryKey: ['regimeBypass'],
    queryFn: tradingService.getRegimeBypass,
  });

  // Check for reconciliation mismatch
  const positionMismatch = openAggregates.length !== brokerPositions.length;

  // Log any errors
  if (aggregatesError) {
    console.error('[OpenPositions] Query error:', aggregatesError);
  }

  // Sync positions mutation
  const syncPositionsMutation = useMutation({
    mutationFn: async () => {
      console.log('[Sync] Starting position sync...');
      const result = await accountsService.syncAllAccounts();
      console.log('[Sync] Sync completed:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('[Sync] Success - synced accounts:', data.accountsSynced, 'positions:', data.positionsSynced);
      // Invalidate all relevant queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['tradeAggregates'] });
      queryClient.invalidateQueries({ queryKey: ['brokerPositions'] });
      queryClient.invalidateQueries({ queryKey: ['webhookStocks'] });
      queryClient.invalidateQueries({ queryKey: ['accounts'] });
    },
    onError: (error: any) => {
      console.error('[Sync] Error:', error);
      alert(error.response?.data?.error || 'Failed to sync positions from broker');
    },
  });

  // Block/unblock mutation
  const blockMutation = useMutation({
    mutationFn: ({ configId, ticker, blocked }: { configId: string; ticker: string; blocked: boolean }) =>
      tradingService.blockStock(configId, ticker, blocked),
    onSuccess: (_, { configId, ticker, blocked }) => {
      // Immediately update the cache so the UI reflects the change without waiting for a refetch
      queryClient.setQueryData<WebhookStock[]>(['webhookStocks'], (old) => {
        if (!old) return old;
        return old.map(stock =>
          stock.configId === configId && stock.ticker === ticker
            ? { ...stock, isBlocked: blocked }
            : stock
        );
      });
      // Also invalidate to ensure fresh data from server
      queryClient.invalidateQueries({ queryKey: ['webhookStocks'] });
      setConfirmBlock(null);
      // Show success feedback
      const msg = blocked
        ? `${ticker} has been blocked`
        : `${ticker} has been unblocked`;
      setBlockSuccessMessage(msg);
      setTimeout(() => setBlockSuccessMessage(null), 3000);
    },
    onError: (error: any) => {
      alert(error.message || 'Failed to update stock blocking status');
    },
  });

  // Regime-guard override toggle mutation
  const regimeBypassMutation = useMutation({
    mutationFn: (enabled: boolean) => tradingService.setRegimeBypass(enabled),
    onSuccess: (_data, enabled) => {
      queryClient.invalidateQueries({ queryKey: ['regimeBypass'] });
      queryClient.invalidateQueries({ queryKey: ['webhookStocks'] });
      setBlockSuccessMessage(
        enabled
          ? 'Regime override ON — buys continue during cautious/adverse regimes'
          : 'Regime override OFF — buys pause during cautious/adverse regimes'
      );
      setTimeout(() => setBlockSuccessMessage(null), 3500);
    },
    onError: (error: any) => {
      alert(error.message || 'Failed to update regime override');
    },
  });

  // Update ticker units mutation
  const updateUnitsMutation = useMutation({
    mutationFn: ({ configId, ticker, units }: { configId: string; ticker: string; units: number | null }) =>
      tradingService.updateTickerUnits(configId, ticker, units),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhookStocks'] });
      setEditingUnitsKey(null);
      setEditUnitsValue('');
    },
    onError: (error: any) => {
      alert(error.response?.data?.error || 'Failed to update units');
    },
  });

  // Sell position mutation
  const sellMutation = useMutation({
    mutationFn: async (stock: WebhookStock & { sellPrice?: number }) => {
      // Step 1: Search for symbol to get universalSymbolId
      const symbolResults = await tradingService.searchSymbol(stock.ticker);

      if (!symbolResults.symbols?.length) {
        throw new Error(`Symbol ${stock.ticker} not found. Please try again.`);
      }

      const symbolMatch = symbolResults.symbols[0];

      // Step 2: Place sell order via existing endpoint
      // ALWAYS use Market order, but pass price for manual stamping in trades table
      return tradingService.placeTrade({
        accountId: stock.accountId,
        ticker: stock.ticker,
        universalSymbolId: symbolMatch.id,
        symbolDescription: symbolMatch.description,
        action: 'SELL',
        units: stock.currentPosition,
        orderType: 'Market', // Always market order for immediate execution
        timeInForce: 'Day',
        price: stock.sellPrice, // Manual price for trades table (not for SnapTrade order type)
      });
    },
    onSuccess: (data, stock) => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['webhookStocks'] });
      queryClient.invalidateQueries({ queryKey: ['tradeAggregates'] });
      queryClient.invalidateQueries({ queryKey: ['brokerPositions'] });
      
      // Close dialog and clear error
      setSellStock(null);
      setSellError(null);
      
      console.log(`Successfully sold ${stock.currentPosition} shares of ${stock.ticker}`);
    },
    onError: (error: any) => {
      console.error('Sell failed:', error);
      // Set error message to display in dialog
      const errorMessage = error.response?.data?.error || error.message || 'Failed to place sell order';
      setSellError(errorMessage);
    },
  });

  // Filter stocks based on selected filter (for All Webhook Stocks table)
  const filteredStocks = stocks.filter((stock) => {
    if (filter === 'active') return !stock.isBlocked;
    if (filter === 'blocked') return stock.isBlocked;
    return true; // 'all'
  });

  const handleBlockClick = (stock: WebhookStock, blocked: boolean) => {
    setConfirmBlock({ stock, blocked });
  };

  const confirmBlockAction = () => {
    if (!confirmBlock) return;
    blockMutation.mutate({
      configId: confirmBlock.stock.configId,
      ticker: confirmBlock.stock.ticker,
      blocked: confirmBlock.blocked,
    });
  };

  const handleSellClick = (stock: WebhookStock) => {
    setSellStock(stock);
    setSellError(null); // Clear any previous errors
  };

  // Handle sell from positions table (trade_aggregates)
  const handleSellPosition = (agg: TradeAggregate, accountName: string, brokerageName: string) => {
    const quantity = agg.buyQtyTotal - agg.sellQtyTotal;
    // Convert aggregate to WebhookStock format for the sell mutation
    const stockForSell: WebhookStock = {
      ticker: agg.ticker,
      accountId: agg.accountId,
      accountName: accountName,
      brokerageName: brokerageName,
      algorithmType: 'Algorithm', // From trade_aggregates
      configId: '', // No config needed for sell
      isConfigActive: false,
      currentPosition: quantity,
      totalTrades: 0,
      lastTradeDate: null,
      totalRealizedPnl: 0,
      winRate: 0,
      totalWins: 0,
      totalLosses: 0,
      isBlocked: false,
      unitOverride: null,
      defaultUnits: 0,
    };
    setSellStock(stockForSell);
    setSellError(null);
  };

  const handleConfirmSell = (quantity: number, price?: number) => {
    if (!sellStock) return;
    // Update the stock quantity to the user-specified amount and add price if provided
    const updatedStock = { ...sellStock, currentPosition: quantity, sellPrice: price };
    sellMutation.mutate(updatedStock);
  };

  const handleCloseSellDialog = () => {
    if (!sellMutation.isPending) {
      setSellStock(null);
      setSellError(null);
    }
  };

  const toggleColumn = (column: keyof ColumnVisibility) => {
    setColumnVisibility(prev => ({ ...prev, [column]: !prev[column] }));
  };

  const columnLabels: Record<keyof ColumnVisibility, string> = {
    ticker: 'Ticker',
    account: 'Account',
    algorithm: 'Algorithm',
    units: 'Units',
    position: 'Position',
    winRate: 'Win Rate',
    totalPnl: 'Total P&L',
    lastTrade: 'Last Trade',
    trades: 'Trades',
    actions: 'Actions',
  };

  // Units editing handlers
  const handleSetWebhookMode = (stock: WebhookStock) => {
    // Clear override - will use webhook payload
    updateUnitsMutation.mutate({
      configId: stock.configId,
      ticker: stock.ticker,
      units: null,
    });
    setEditingUnitsKey(null);
  };

  const handleSetCustomMode = (stock: WebhookStock) => {
    const key = `${stock.ticker}-${stock.configId}`;
    setEditingUnitsKey(key);
    setEditUnitsValue(stock.unitOverride !== null ? stock.unitOverride.toString() : '');
  };

  const handleEditUnitsCancel = () => {
    setEditingUnitsKey(null);
    setEditUnitsValue('');
  };

  const handleEditUnitsSave = (stock: WebhookStock) => {
    const numValue = parseFloat(editUnitsValue);
    if (!isNaN(numValue) && numValue > 0) {
      updateUnitsMutation.mutate({
        configId: stock.configId,
        ticker: stock.ticker,
        units: numValue,
      });
    } else {
      handleEditUnitsCancel();
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">Loading webhook stocks...</div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 py-8">Error loading webhook stocks: {(error as Error).message}</div>
    );
  }

  // Helper to get account info
  const getAccountInfo = (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    return {
      accountName: account?.accountName || 'Unknown Account',
      brokerageName: account?.brokerageName || 'Unknown Broker',
    };
  };

  return (
    <div>
      {/* Reconciliation Warning Banner */}
      {positionMismatch && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-amber-500 mr-3 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-sm font-medium text-amber-800 dark:text-amber-200">
                Position Mismatch Detected
              </h3>
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">
                Your algorithm positions ({openAggregates.length}) don't match broker positions ({brokerPositions.length}). 
                This may indicate a sync issue or external trades. Click "Refresh Positions" to sync from broker.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Open Positions Section - Always Visible */}
      <div className="mb-8">
        <div className="mb-4">
          <h2 className="text-xl font-medium text-gray-900 dark:text-white">
            Open Positions ({openAggregates.length})
          </h2>
        </div>

        {isLoadingAggregates ? (
          <div className="bg-white dark:bg-dark-card shadow rounded-lg p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading positions...</p>
          </div>
        ) : openAggregates.length > 0 ? (
          <>
            {/* Mobile View - Simplified clickable rows */}
            <div className="sm:hidden bg-white dark:bg-dark-card shadow rounded-lg overflow-hidden border-2 border-blue-500">
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {openAggregates.map((agg) => {
                  const quantity = agg.buyQtyTotal - agg.sellQtyTotal;
                  const { accountName, brokerageName } = getAccountInfo(agg.accountId);
                  const avgPrice = agg.buyQtyTotal > 0 ? agg.buyNotionalTotal / agg.buyQtyTotal : 0;
                  
                  return (
                    <button
                      key={`open-mobile-${agg.tradeKey}`}
                      onClick={() => setSelectedPosition({ agg, accountName, brokerageName, quantity, avgPrice })}
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0" />
                        <div>
                          <div className="text-base font-semibold text-gray-900 dark:text-white">{agg.ticker}</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {quantity.toFixed(2)} shares
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Desktop View - Full table */}
            <div className="hidden sm:block bg-white dark:bg-dark-card shadow rounded-lg overflow-hidden border-2 border-blue-500">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticker
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Quantity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Account
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Cost Basis
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Opened
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-gray-700">
                {openAggregates.map((agg) => {
                  const quantity = agg.buyQtyTotal - agg.sellQtyTotal;
                  const { accountName, brokerageName } = getAccountInfo(agg.accountId);
                  const avgPrice = agg.buyQtyTotal > 0 ? agg.buyNotionalTotal / agg.buyQtyTotal : 0;
                  
                  return (
                    <tr key={`open-${agg.tradeKey}`} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      {/* Ticker */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <div
                            className="w-2 h-2 rounded-full bg-green-500"
                            title="Open Position"
                          />
                          <div className="text-lg font-bold text-gray-900 dark:text-white">{agg.ticker}</div>
                        </div>
                      </td>

                      {/* Quantity */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {quantity.toFixed(2)} shares
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          @ ${avgPrice.toFixed(2)}/share
                        </div>
                      </td>

                      {/* Account */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">{accountName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{brokerageName}</div>
                      </td>

                      {/* Cost Basis */}
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          ${agg.buyNotionalTotal.toFixed(2)}
                        </div>
                      </td>

                      {/* Opened */}
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {new Date(agg.openTime).toLocaleDateString('en-US', {
                            timeZone: 'America/Los_Angeles',
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                          })}
                        </div>
                      </td>

                      {/* Actions - Sell Button */}
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleSellPosition(agg, accountName, brokerageName)}
                          disabled={sellMutation.isPending}
                          className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Sell {quantity.toFixed(2)}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

            {/* Mobile Position Detail Bottom Sheet */}
            <BottomSheet
              isOpen={selectedPosition !== null}
              onClose={() => setSelectedPosition(null)}
              title={selectedPosition?.agg.ticker}
              footer={
                <button
                  onClick={() => {
                    if (selectedPosition) {
                      handleSellPosition(selectedPosition.agg, selectedPosition.accountName, selectedPosition.brokerageName);
                      setSelectedPosition(null);
                    }
                  }}
                  disabled={sellMutation.isPending}
                  className="w-full py-3 rounded-xl text-base font-semibold bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Sell {selectedPosition?.quantity.toFixed(2)} Shares
                </button>
              }
            >
              {selectedPosition && (
                <div className="space-y-4">
                  {/* Quantity */}
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                    <span className="text-gray-500 dark:text-gray-400">Quantity</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {selectedPosition.quantity.toFixed(2)} shares
                    </span>
                  </div>

                  {/* Avg Price */}
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                    <span className="text-gray-500 dark:text-gray-400">Avg Price</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${selectedPosition.avgPrice.toFixed(2)}
                    </span>
                  </div>

                  {/* Cost Basis */}
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                    <span className="text-gray-500 dark:text-gray-400">Cost Basis</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      ${selectedPosition.agg.buyNotionalTotal.toFixed(2)}
                    </span>
                  </div>

                  {/* Account */}
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                    <span className="text-gray-500 dark:text-gray-400">Account</span>
                    <div className="text-right">
                      <div className="font-medium text-gray-900 dark:text-white">{selectedPosition.accountName}</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">{selectedPosition.brokerageName}</div>
                    </div>
                  </div>

                  {/* Opened */}
                  <div className="flex justify-between items-center py-3">
                    <span className="text-gray-500 dark:text-gray-400">Opened</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {new Date(selectedPosition.agg.openTime).toLocaleDateString('en-US', {
                        timeZone: 'America/Los_Angeles',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </div>
              )}
            </BottomSheet>
          </>
        ) : (
          <div className="bg-white dark:bg-dark-card shadow rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No Open Positions</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              You don't currently have any open algorithm positions.
            </p>
            <p className="mt-2 text-xs text-gray-400 dark:text-gray-500">
              Positions opened through algorithm trading will appear here.
            </p>
          </div>
        )}

        {/* Refresh Positions Button - Full width on mobile */}
        <button
          onClick={() => syncPositionsMutation.mutate()}
          disabled={syncPositionsMutation.isPending || isLoadingAggregates}
          className="mt-4 w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
          title="Sync positions from broker to update this list"
        >
          <RefreshCw className={`w-4 h-4 ${syncPositionsMutation.isPending ? 'animate-spin' : ''}`} />
          <span>{syncPositionsMutation.isPending ? 'Syncing...' : 'Refresh Positions'}</span>
        </button>
      </div>

      {/* Block/Unblock Success Banner */}
      {blockSuccessMessage && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center justify-between">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span className="text-sm font-medium text-green-800 dark:text-green-200">{blockSuccessMessage}</span>
          </div>
          <button
            onClick={() => setBlockSuccessMessage(null)}
            className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Regime-Guard Override Toggle */}
      {(() => {
        const regimeEnabled = regimeBypass?.enabled ?? false;
        const currentRegime = regimeBypass?.regime ?? null;
        const regimePaused = currentRegime === 'adverse' || currentRegime === 'cautious';
        return (
          <div className="mb-6 flex items-center justify-between p-4 bg-white dark:bg-dark-card shadow rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <ShieldAlert className={`w-5 h-5 flex-shrink-0 mt-0.5 ${regimeEnabled ? 'text-amber-500' : 'text-gray-400'}`} />
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-white flex items-center gap-2">
                  Trade during adverse market regime
                  {currentRegime && (
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase ${
                        regimePaused
                          ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
                          : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                      }`}
                      title="Current market regime"
                    >
                      {currentRegime}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-md">
                  {regimeEnabled
                    ? 'ON — webhook BUYs keep trading even when the market regime is cautious/adverse. Other blocks (blocked tickers, pre-close window, circuit breaker) still apply.'
                    : regimePaused
                      ? `BUYs are currently PAUSED because the regime is ${currentRegime}. Turn this on to keep buying anyway.`
                      : 'When off, BUYs pause automatically while the market regime is cautious/adverse. SELLs always go through.'}
                </div>
              </div>
            </div>
            <button
              onClick={() => regimeBypassMutation.mutate(!regimeEnabled)}
              disabled={regimeBypassMutation.isPending}
              aria-label={regimeEnabled ? 'Disable regime override' : 'Enable regime override'}
              className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors disabled:opacity-50 flex-shrink-0 ${
                regimeEnabled ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  regimeEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        );
      })()}

      {/* All Webhook Stocks Section */}
      <div className="mb-4">
        <h2 className="text-xl font-medium text-gray-900 dark:text-white mb-4">
          All Webhook Stocks
        </h2>
      </div>

      {/* Filters and Column Controls */}
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-300">Filter:</span>
          <div className="flex gap-1.5 flex-wrap">
            <button
              onClick={() => setFilter('all')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              All ({stocks.length})
            </button>
            <button
              onClick={() => setFilter('active')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === 'active'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Active ({stocks.filter((s) => !s.isBlocked).length})
            </button>
            <button
              onClick={() => setFilter('blocked')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                filter === 'blocked'
                  ? 'bg-red-600 text-white'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Blocked ({stocks.filter((s) => s.isBlocked).length})
            </button>
          </div>
        </div>

        {/* Action Buttons - Column selector hidden on mobile */}
        <div className="hidden sm:flex items-center gap-2">
          {/* New Trade Button */}
          <button
            onClick={() => setIsTradeDialogOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
          >
            <Plus className="w-4 h-4" />
            <span>New Trade</span>
          </button>

          {/* Column Visibility Menu */}
          <div className="relative">
            <button
              onClick={() => setShowColumnMenu(!showColumnMenu)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
              </svg>
              <span>Columns</span>
            </button>

            {showColumnMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowColumnMenu(false)}
                />
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg z-20 border border-gray-200 dark:border-gray-700">
                  <div className="p-2">
                    <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                      Show Columns
                    </div>
                    {Object.entries(columnVisibility).map(([key, visible]) => (
                      <button
                        key={key}
                        onClick={() => toggleColumn(key as keyof ColumnVisibility)}
                        className="w-full flex items-center justify-between px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                      >
                        <span>{columnLabels[key as keyof ColumnVisibility]}</span>
                        <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${
                          visible
                            ? 'bg-blue-600 border-blue-600'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}>
                          {visible && (
                            <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Empty State */}
      {filteredStocks.length === 0 && (
        <div className="bg-white dark:bg-dark-card shadow rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No webhook stocks found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {filter === 'all'
              ? 'No stocks have been traded via webhooks yet.'
              : filter === 'active'
              ? 'No active stocks. All stocks are currently blocked.'
              : 'No blocked stocks.'}
          </p>
        </div>
      )}

      {/* Stocks Table */}
      {filteredStocks.length > 0 && (
        <>
          {/* Mobile View - Robinhood-style open list */}
          <div className="sm:hidden -mx-5">
            {filteredStocks.map((stock, index) => (
              <button
                key={`mobile-${stock.ticker}-${stock.configId}`}
                onClick={() => setSelectedStock(stock)}
                className={`w-full flex items-center justify-between px-5 py-4 active:bg-gray-100 dark:active:bg-gray-800/50 transition-colors text-left ${
                  stock.isBlocked ? 'opacity-60' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  {/* Emoji based on P&L */}
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center text-2xl">
                    {stock.totalRealizedPnl >= 0 ? '😜' : '😭'}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`text-base font-medium ${
                        stock.isBlocked
                          ? 'text-gray-400 dark:text-gray-500'
                          : 'text-gray-900 dark:text-white'
                      }`}>
                        {stock.ticker}
                      </span>
                      {stock.isBlocked ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 uppercase">
                          Blocked
                        </span>
                      ) : (
                        <div className="w-2 h-2 rounded-full bg-green-500" />
                      )}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {stock.totalTrades} trades
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <div
                      className={`text-base font-medium ${
                        stock.totalRealizedPnl > 0
                          ? 'text-green-600 dark:text-green-400'
                          : stock.totalRealizedPnl < 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      {stock.totalRealizedPnl >= 0 ? '+' : ''}${stock.totalRealizedPnl.toFixed(2)}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-300 dark:text-gray-600" />
                </div>
              </button>
            ))}
          </div>

          {/* Mobile Webhook Stock Detail Bottom Sheet */}
          <BottomSheet
            isOpen={selectedStock !== null}
            onClose={() => setSelectedStock(null)}
            title={selectedStock?.ticker}
            footer={
              <button
                onClick={() => {
                  if (selectedStock) {
                    handleBlockClick(selectedStock, !selectedStock.isBlocked);
                    setSelectedStock(null);
                  }
                }}
                disabled={blockMutation.isPending}
                className={`w-full py-3 rounded-xl text-base font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  selectedStock?.isBlocked
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                {selectedStock?.isBlocked ? 'Unblock Stock' : 'Block Stock'}
              </button>
            }
          >
            {selectedStock && (
              <div className="space-y-4">
                {/* Status */}
                <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400">Status</span>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    selectedStock.isBlocked
                      ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                      : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                  }`}>
                    {selectedStock.isBlocked ? 'Blocked' : 'Active'}
                  </span>
                </div>

                {/* Total P&L */}
                <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400">Total P&L</span>
                  <span className={`font-medium ${
                    selectedStock.totalRealizedPnl > 0
                      ? 'text-green-600 dark:text-green-400'
                      : selectedStock.totalRealizedPnl < 0
                      ? 'text-red-600 dark:text-red-400'
                      : 'text-gray-900 dark:text-white'
                  }`}>
                    ${selectedStock.totalRealizedPnl.toFixed(2)}
                  </span>
                </div>

                {/* Total Trades */}
                <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400">Total Trades</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedStock.totalTrades}
                  </span>
                </div>

                {/* Win Rate */}
                <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400">Win Rate</span>
                  <div className="text-right">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {selectedStock.winRate.toFixed(1)}%
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {selectedStock.totalWins}W / {selectedStock.totalLosses}L
                    </div>
                  </div>
                </div>

                {/* Current Position */}
                {selectedStock.currentPosition > 0 && (
                  <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                    <span className="text-gray-500 dark:text-gray-400">Current Position</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {selectedStock.currentPosition} shares
                    </span>
                  </div>
                )}

                {/* Account */}
                <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400">Account</span>
                  <div className="text-right">
                    <div className="font-medium text-gray-900 dark:text-white">{selectedStock.accountName}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{selectedStock.brokerageName}</div>
                  </div>
                </div>

                {/* Algorithm */}
                <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400">Algorithm</span>
                  <div className="text-right">
                    <div className="font-medium text-gray-900 dark:text-white">{selectedStock.algorithmType}</div>
                    <div className="text-xs">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                        selectedStock.isConfigActive
                          ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}>
                        {selectedStock.isConfigActive ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Units - Editable */}
                <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-gray-500 dark:text-gray-400">Units Mode</span>
                  {editingUnitsKey === `${selectedStock.ticker}-${selectedStock.configId}` ? (
                    // Edit mode: show input
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={editUnitsValue}
                        onChange={(e) => setEditUnitsValue(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleEditUnitsSave(selectedStock);
                          if (e.key === 'Escape') handleEditUnitsCancel();
                        }}
                        placeholder="units"
                        className="w-20 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                        autoFocus
                        min="0.01"
                        step="any"
                      />
                      <button
                        onClick={() => handleEditUnitsSave(selectedStock)}
                        disabled={updateUnitsMutation.isPending || !editUnitsValue}
                        className="p-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                        title="Save"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                      <button
                        onClick={handleEditUnitsCancel}
                        className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                        title="Cancel"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ) : (
                    // Display mode: show value with edit options
                    <div className="flex items-center gap-2">
                      <select
                        value={selectedStock.unitOverride !== null ? 'custom' : 'webhook'}
                        onChange={(e) => {
                          if (e.target.value === 'webhook') {
                            handleSetWebhookMode(selectedStock);
                          } else {
                            handleSetCustomMode(selectedStock);
                          }
                        }}
                        disabled={updateUnitsMutation.isPending}
                        className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                      >
                        <option value="webhook">Webhook</option>
                        <option value="custom">
                          {selectedStock.unitOverride !== null ? `Custom (${selectedStock.unitOverride})` : 'Custom...'}
                        </option>
                      </select>
                    </div>
                  )}
                </div>

                {/* Last Trade */}
                <div className="flex justify-between items-center py-3">
                  <span className="text-gray-500 dark:text-gray-400">Last Trade</span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {selectedStock.lastTradeDate
                      ? new Date(selectedStock.lastTradeDate).toLocaleDateString('en-US', {
                          timeZone: 'America/Los_Angeles',
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })
                      : 'N/A'}
                  </span>
                </div>
              </div>
            )}
          </BottomSheet>

          {/* Desktop View - Full table */}
          <div className="hidden sm:block bg-white dark:bg-dark-card shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                {columnVisibility.ticker && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Ticker
                  </th>
                )}
                {columnVisibility.account && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Account
                  </th>
                )}
                {columnVisibility.algorithm && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Algorithm
                  </th>
                )}
                {columnVisibility.units && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Units
                  </th>
                )}
                {columnVisibility.position && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Position
                  </th>
                )}
                {columnVisibility.winRate && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Win Rate
                  </th>
                )}
                {columnVisibility.totalPnl && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total P&L
                  </th>
                )}
                {columnVisibility.lastTrade && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Trade
                  </th>
                )}
                {columnVisibility.trades && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Trades
                  </th>
                )}
                {columnVisibility.actions && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-dark-card divide-y divide-gray-200 dark:divide-gray-700">
              {filteredStocks.map((stock) => (
                <tr key={`${stock.ticker}-${stock.configId}`} className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${stock.isBlocked ? 'opacity-60' : ''}`}>
                  {/* Ticker with Status */}
                  {columnVisibility.ticker && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {stock.isBlocked ? (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 uppercase">
                            Blocked
                          </span>
                        ) : (
                          <div
                            className="w-2 h-2 rounded-full bg-green-500"
                            title="Active"
                          />
                        )}
                        <div className={`text-lg font-bold ${stock.isBlocked ? 'text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white'}`}>{stock.ticker}</div>
                      </div>
                    </td>
                  )}

                  {/* Account */}
                  {columnVisibility.account && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{stock.accountName}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{stock.brokerageName}</div>
                    </td>
                  )}

                  {/* Algorithm */}
                  {columnVisibility.algorithm && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{stock.algorithmType}</div>
                      <div className="text-xs">
                        {stock.isConfigActive ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200">
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                            Inactive
                          </span>
                        )}
                      </div>
                    </td>
                  )}

                  {/* Units (dropdown: Webhook or Custom) */}
                  {columnVisibility.units && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      {editingUnitsKey === `${stock.ticker}-${stock.configId}` ? (
                        // Custom mode: show input
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            value={editUnitsValue}
                            onChange={(e) => setEditUnitsValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleEditUnitsSave(stock);
                              if (e.key === 'Escape') handleEditUnitsCancel();
                            }}
                            placeholder="units"
                            className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                            autoFocus
                            min="0.01"
                            step="any"
                          />
                          <button
                            onClick={() => handleEditUnitsSave(stock)}
                            disabled={updateUnitsMutation.isPending || !editUnitsValue}
                            className="p-1 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50"
                            title="Save"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={handleEditUnitsCancel}
                            className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                            title="Cancel"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        // Display mode: dropdown
                        <select
                          value={stock.unitOverride !== null ? 'custom' : 'webhook'}
                          onChange={(e) => {
                            if (e.target.value === 'webhook') {
                              handleSetWebhookMode(stock);
                            } else {
                              handleSetCustomMode(stock);
                            }
                          }}
                          disabled={updateUnitsMutation.isPending}
                          className="text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 cursor-pointer"
                        >
                          <option value="webhook">Webhook</option>
                          <option value="custom">
                            {stock.unitOverride !== null ? `Custom (${stock.unitOverride})` : 'Custom...'}
                          </option>
                        </select>
                      )}
                    </td>
                  )}

                  {/* Current Position */}
                  {columnVisibility.position && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {stock.currentPosition > 0 && (
                          <svg
                            className="w-4 h-4 text-yellow-500 mr-1"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                        <span className="text-sm text-gray-900 dark:text-white">
                          {stock.currentPosition > 0 ? `${stock.currentPosition} shares` : 'None'}
                        </span>
                      </div>
                    </td>
                  )}

                  {/* Win Rate */}
                  {columnVisibility.winRate && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">
                        {stock.winRate.toFixed(1)}%
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {stock.totalWins}W / {stock.totalLosses}L
                      </div>
                    </td>
                  )}

                  {/* Total P&L */}
                  {columnVisibility.totalPnl && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div
                        className={`text-sm font-medium ${
                          stock.totalRealizedPnl > 0
                            ? 'text-green-600 dark:text-green-400'
                            : stock.totalRealizedPnl < 0
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        ${stock.totalRealizedPnl.toFixed(2)}
                      </div>
                    </td>
                  )}

                  {/* Last Trade */}
                  {columnVisibility.lastTrade && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {stock.lastTradeDate
                        ? new Date(stock.lastTradeDate).toLocaleDateString('en-US', {
                            timeZone: 'America/Los_Angeles',
                            month: 'short',
                            day: 'numeric',
                          })
                        : 'N/A'}
                    </td>
                  )}

                  {/* Total Trades */}
                  {columnVisibility.trades && (
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {stock.totalTrades}
                    </td>
                  )}

                  {/* Actions */}
                  {columnVisibility.actions && (
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => handleBlockClick(stock, !stock.isBlocked)}
                        disabled={blockMutation.isPending}
                        className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                          stock.isBlocked
                            ? 'bg-green-600 hover:bg-green-700 text-white'
                            : 'bg-red-600 hover:bg-red-700 text-white'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {stock.isBlocked ? 'Unblock' : 'Block'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Register Webhook Button - Full width on mobile */}
      <button
        onClick={() => setIsTradeDialogOpen(true)}
        className="mt-4 w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm font-medium"
      >
        <Plus className="w-4 h-4" />
        <span>Register Webhook</span>
      </button>

      {/* Sell Position Dialog */}
      <SellPositionDialog
        isOpen={sellStock !== null}
        stock={sellStock}
        onClose={handleCloseSellDialog}
        onConfirm={handleConfirmSell}
        isLoading={sellMutation.isPending}
        error={sellError}
      />

      {/* Block Confirmation Dialog */}
      {confirmBlock && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-card rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">
              {confirmBlock.blocked ? 'Block' : 'Unblock'} {confirmBlock.stock.ticker}?
            </h3>

            {confirmBlock.blocked && confirmBlock.stock.currentPosition > 0 && (
              <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                <div className="flex">
                  <svg
                    className="h-5 w-5 text-yellow-400 mr-2 flex-shrink-0"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <div className="text-sm text-yellow-800 dark:text-yellow-200">
                    <strong>Warning:</strong> You currently hold {confirmBlock.stock.currentPosition} shares of{' '}
                    {confirmBlock.stock.ticker}. Blocking will only prevent NEW trades from webhooks. Your existing
                    position will not be affected.
                  </div>
                </div>
              </div>
            )}

            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              {confirmBlock.blocked
                ? `This will prevent future webhook signals for ${confirmBlock.stock.ticker} from executing trades on your ${confirmBlock.stock.accountName} account. All historical data will be preserved.`
                : `This will allow webhook signals for ${confirmBlock.stock.ticker} to execute trades again on your ${confirmBlock.stock.accountName} account.`}
            </p>

            <div className="flex space-x-3">
              <button
                onClick={() => setConfirmBlock(null)}
                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmBlockAction}
                disabled={blockMutation.isPending}
                className={`flex-1 px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                  confirmBlock.blocked
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {blockMutation.isPending
                  ? 'Processing...'
                  : confirmBlock.blocked
                  ? 'Block Stock'
                  : 'Unblock Stock'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

